"use client";

import { clsx } from "clsx";
import { useContactSalesFormV2 } from "./hooks/useContactSalesFormV2";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import { ResumePrompt } from "./components/ResumePrompt";
import { StepNavigation } from "./components/StepNavigation";
import { CompactFormCard } from "./CompactFormCard";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";

const FADE_VARIANTS = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
} as const;

export function ContactSalesFormV2({ compact = false }: { compact?: boolean }) {
  const { form, step, stepIndex, showResumePrompt, continueWithSavedForm, startFreshForm, steps } =
    useContactSalesFormV2();

  const currentStepData = steps[stepIndex];

  if (compact) {
    return (
      <CompactForm
        form={form}
        step={step}
        currentStepData={currentStepData}
        showResumePrompt={showResumePrompt}
        continueWithSavedForm={continueWithSavedForm}
        startFreshForm={startFreshForm}
        steps={steps}
      />
    );
  }

  return (
    <DefaultForm
      form={form}
      step={step}
      currentStepData={currentStepData}
      showResumePrompt={showResumePrompt}
      continueWithSavedForm={continueWithSavedForm}
      startFreshForm={startFreshForm}
      steps={steps}
    />
  );
}

type FormVariantProps = {
  form: ReturnType<typeof useContactSalesFormV2>["form"];
  step: number;
  currentStepData: ReturnType<typeof useContactSalesFormV2>["steps"][number];
  showResumePrompt: boolean;
  continueWithSavedForm: () => void;
  startFreshForm: () => void;
  steps: ReturnType<typeof useContactSalesFormV2>["steps"];
};

function DefaultForm({
  form,
  step,
  currentStepData,
  showResumePrompt,
  continueWithSavedForm,
  startFreshForm,
  steps,
}: FormVariantProps) {
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <div className='tw-mb-8'>
        <AnimatePresence mode='wait'>
          <div key={`header-${step}`}>
            <m.h2
              className='tw-type-5xl tw-mb-6'
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {currentStepData.title}
            </m.h2>
            <m.p
              className='tw-type-base tw-mb-9'
              initial={false}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {currentStepData.subtitle}
            </m.p>
          </div>
        </AnimatePresence>
      </div>
      <form.Subscribe selector={state => state.isSubmitting}>
        {isSubmitting => (
          <div className='tw-relative'>
            <AnimatePresence mode='wait'>
              <m.div
                key={`step-${step}`}
                initial={false}
                animate='animate'
                exit='exit'
                variants={FADE_VARIANTS}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={
                    isSubmitting
                      ? "tw-opacity-30 tw-pointer-events-none tw-transition-opacity tw-duration-300"
                      : "tw-transition-opacity tw-duration-300"
                  }
                >
                  {currentStepData.component}
                </div>
              </m.div>
            </AnimatePresence>
            {isSubmitting && (
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='tw-absolute tw-top-1/3 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2'
              >
                <Spinner20 className='tw-text-gray-900 tw-scale-150' />
              </m.div>
            )}
          </div>
        )}
      </form.Subscribe>
      <AnimatePresence mode='wait'>
        <m.div
          key={`navigation-${step}`}
          initial={false}
          animate='animate'
          exit='exit'
          variants={FADE_VARIANTS}
          transition={{ duration: 0.2 }}
        >
          <StepNavigation
            navigation={currentStepData.navigation}
            currentStep={step}
            totalSteps={steps.length}
            form={form}
          />
        </m.div>
      </AnimatePresence>
      <ResumePrompt show={showResumePrompt} onContinue={continueWithSavedForm} onStartFresh={startFreshForm} />
    </form>
  );
}

function CompactForm({
  form,
  step,
  currentStepData,
  showResumePrompt,
  continueWithSavedForm,
  startFreshForm,
  steps,
}: FormVariantProps) {
  return (
    <CompactFormCard>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className='tw-mb-4 sm:tw-mb-6 lg:tw-mb-8'>
          <AnimatePresence mode='wait'>
            <div key={`compact-header-${step}`}>
              <m.h2
                className='tw-type-3xl sm:tw-type-4xl lg:tw-type-5xl tw-mb-3 sm:tw-mb-4 lg:tw-mb-6'
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {currentStepData.title}
              </m.h2>
              <m.p
                className='tw-type-sm sm:tw-type-base tw-mb-8 sm:tw-mb-12 tw-opacity-60 lg:tw-mb-9'
                initial={false}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                {currentStepData.subtitle}
              </m.p>
            </div>
          </AnimatePresence>
        </div>
        <form.Subscribe selector={state => state.isSubmitting}>
          {isSubmitting => (
            <div className='tw-relative'>
              <AnimatePresence mode='wait'>
                <m.div
                  key={`compact-step-${step}`}
                  className='max-lg:tw-animate-in max-lg:tw-fade-in max-lg:tw-duration-200'
                  initial={false}
                  animate='animate'
                  exit='exit'
                  variants={FADE_VARIANTS}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className={clsx(
                      "tw-transition-opacity tw-duration-300",
                      isSubmitting && "tw-opacity-30 tw-pointer-events-none",
                    )}
                  >
                    {currentStepData.component}
                  </div>
                </m.div>
              </AnimatePresence>
              {isSubmitting && (
                <m.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='tw-absolute tw-top-1/3 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2'
                >
                  <Spinner20 className='tw-text-gray-900 tw-scale-150' />
                </m.div>
              )}
            </div>
          )}
        </form.Subscribe>
        <AnimatePresence mode='wait'>
          <m.div
            key={`compact-nav-${step}`}
            initial={false}
            animate='animate'
            exit='exit'
            variants={FADE_VARIANTS}
            transition={{ duration: 0.2 }}
          >
            <StepNavigation
              navigation={currentStepData.navigation}
              currentStep={step}
              totalSteps={steps.length}
              form={form}
              pinnedFooter
            />
          </m.div>
        </AnimatePresence>
        <ResumePrompt
          show={showResumePrompt}
          onContinue={continueWithSavedForm}
          onStartFresh={startFreshForm}
          disableAnimation
        />
      </form>
    </CompactFormCard>
  );
}
